import { useState, useCallback, useMemo, useEffect } from 'react'
import type { QuizSolveResult, BaseQuizData } from '../../../types/legacyQuiz'

export interface CountingQuizData extends BaseQuizData {
  type: 'counting'
  digits: number
}

export interface UseCountingQuizOptions {
  quizId: string | number
  quiz: CountingQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  onAnswerChange?: (answers: string[]) => void
}

export function useCountingQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
}: UseCountingQuizOptions) {
  const [startTime] = useState(() => Date.now())
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false)

  const isSolved = solveResult !== null && solveResult !== undefined
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers

  const correctDigits = useMemo(() => {
    const correctStr = solveResult?.correctAnswers?.[0]
    if (!correctStr) return null
    return correctStr.split('').map(Number)
  }, [solveResult?.correctAnswers])

  const computedInitialDigits = useMemo(() => {
    const numDigits = quiz.digits || 1
    if (initialAnswersProp !== undefined) {
      const parsed = (initialAnswersProp[0] ?? '').split('').filter(Boolean).map(Number)
      if (parsed.length >= numDigits) return parsed.slice(0, numDigits)
      return [...parsed, ...Array(numDigits - parsed.length).fill(0)]
    }
    if (isViewSolved && correctDigits) {
      return correctDigits.slice(0, numDigits)
    }
    return Array(numDigits).fill(0)
  }, [quiz.digits, isViewSolved, correctDigits, initialAnswersProp])

  const [digitValues, setDigitValues] = useState<number[]>(computedInitialDigits)
  const canSubmit = true

  const [hasInteracted, setHasInteracted] = useState(false)
  const showLottie = !isSolved && !isViewSolved && !hasInteracted && (!initialAnswersProp || initialAnswersProp.length === 0)

  const displayValues = useMemo(() => {
    if (shouldShowCorrectAnswers && correctDigits) {
      return correctDigits.slice(0, quiz.digits || 1)
    }
    return digitValues
  }, [shouldShowCorrectAnswers, correctDigits, digitValues, quiz.digits])

  useEffect(() => {
    onAnswerChange?.([digitValues.join('')])
  }, [digitValues, onAnswerChange])

  const handleUp = useCallback((index: number) => {
    if (isSolved || isViewSolved) return
    setHasInteracted(true)
    setDigitValues(prev => {
      const next = [...prev]
      next[index] = (next[index] + 1) % 10
      return next
    })
  }, [isSolved, isViewSolved])

  const handleDown = useCallback((index: number) => {
    if (isSolved || isViewSolved) return
    setHasInteracted(true)
    setDigitValues(prev => {
      const next = [...prev]
      next[index] = (next[index] - 1 + 10) % 10
      return next
    })
  }, [isSolved, isViewSolved])

  const handleSubmit = useCallback(() => {
    if (isSolved) return
    const elapsedTime = Date.now() - startTime
    const joinedAnswer = digitValues.join('')
    onSolve?.(quizId, [joinedAnswer], quiz.vars, elapsedTime)
  }, [isSolved, startTime, digitValues, onSolve, quizId, quiz.vars])

  const isDisabled = isSolved || isViewSolved

  return {
    digitValues,
    displayValues,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    correctDigits,
    showLottie,
    isDisabled,
    handleUp,
    handleDown,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved,
  }
}
